import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import type { TioTileField } from '../../api/tomorrowIoTilesService';
import type { RadarTile } from './useDataFetching';
import type { EventWithCircuit } from '../../api/eventsService';
import type { CircuitWithGeoJSON } from '../../api/circuitsService';
import type { StationData } from '../../api/stationObservationsService';
import type { MapTileProvider } from './useNowcastingState';
import { getEventKey } from './nowcastingStorage';
import { resolveMapStyle } from './nowcastingMapIds';
import { useMapLayers } from './useMapLayers';

interface UsePiPMapParams {
  selectedEvent: EventWithCircuit | null;
  isPiPVisible: boolean;
  mapStyle: string | null;
  mapStyles: Record<string, { name: string; url: string }>;
  theme: string;
  circuitData: CircuitWithGeoJSON | null;
  showWindArrows: boolean;
  stations: StationData[];
  tiles: RadarTile[];
  currentIndex: number;
  extendedTiles: RadarTile[];
  mapTileProvider: MapTileProvider;
  tioTimestamps: string[];
  tioCurrentIndex: number;
  tioTileField: TioTileField;
  radarOpacity: number;
  fps: number;
  tilesSource: string;
  trackLineWidth?: number;
  numericBlobUrl?: string | null;
  isCircleOverlayVisible: boolean;
  showRadarBbox: boolean;
  showRadiusCircles: boolean;
  radiusCircleCount: number;
  radiusCircleDistance: number;
}

interface UsePiPMapReturn {
  pipMapContainerRef: React.RefObject<HTMLDivElement | null>;
  pipMapRef: React.RefObject<mapboxgl.Map | null>;
  pipStyleVersion: number;
}

/**
 * Custom hook to manage the Picture-in-Picture (PiP) map view.
 * Handles map initialization, circuit/station rendering, radar layers, and TIO layers.
 */
export function usePiPMap({
  selectedEvent,
  isPiPVisible,
  mapStyle,
  mapStyles,
  theme,
  circuitData,
  showWindArrows,
  stations,
  tiles,
  currentIndex,
  extendedTiles,
  mapTileProvider,
  tioTimestamps,
  tioCurrentIndex,
  tioTileField,
  radarOpacity,
  fps,
  tilesSource,
  trackLineWidth = 3,
  numericBlobUrl,
  isCircleOverlayVisible,
  showRadarBbox,
  showRadiusCircles,
  radiusCircleCount,
  radiusCircleDistance,
}: UsePiPMapParams): UsePiPMapReturn {
  // PiP map refs
  const pipMapContainerRef = useRef<HTMLDivElement | null>(null);
  const pipMapRef = useRef<mapboxgl.Map | null>(null);
  const [pipStyleVersion, setPipStyleVersion] = useState(0);

  // TIO active layer tracking (passed to useMapLayers)
  const pipTioActiveLayerRef = useRef<'layer1' | 'layer2'>('layer1');
  const previousEventKeyRef = useRef<string | null>(null);
  const splitViewReady = selectedEvent != null && isPiPVisible;

  // Create or recreate PiP map when style/theme/visibility changes (not on event switch).
  useEffect(() => {
    if (!pipMapContainerRef.current || !selectedEvent || !isPiPVisible) return;

    const circuitLat = selectedEvent.circuit.latitude || 0;
    const circuitLon = selectedEvent.circuit.longitude || 0;
    const styleUrl = resolveMapStyle(mapStyle, mapStyles, theme);

    const pipMap = new mapboxgl.Map({
      container: pipMapContainerRef.current,
      style: styleUrl,
      center: [circuitLon, circuitLat],
      zoom: 13,
      minZoom: 6,
      interactive: true,
      projection: 'mercator',
      preserveDrawingBuffer: true,
    });

    pipMap.addControl(new mapboxgl.NavigationControl(), 'top-right');

    pipMap.on('load', () => {
      pipMap.resize();
      setPipStyleVersion(v => v + 1);

      setTimeout(() => {
        const attributionButton = pipMapContainerRef.current?.querySelector('.mapboxgl-ctrl-attrib-button') as HTMLElement;
        if (attributionButton) {
          attributionButton.click();
        }
      }, 100);
    });

    pipMapRef.current = pipMap;
    previousEventKeyRef.current = getEventKey(selectedEvent);

    return () => {
      pipMap.remove();
      pipMapRef.current = null;
      previousEventKeyRef.current = null;
    };
  }, [splitViewReady, mapStyle, mapStyles, theme, isPiPVisible]);

  // Reuse PiP map when switching events.
  useEffect(() => {
    const pipMap = pipMapRef.current;
    if (!pipMap || !selectedEvent || !isPiPVisible) return;

    const eventKey = getEventKey(selectedEvent);
    if (previousEventKeyRef.current === eventKey) return;

    previousEventKeyRef.current = eventKey;
    const circuitLat = selectedEvent.circuit.latitude || 0;
    const circuitLon = selectedEvent.circuit.longitude || 0;
    pipMap.jumpTo({ center: [circuitLon, circuitLat], zoom: 13 });
  }, [selectedEvent, isPiPVisible]);

  // All layers via composite hook
  useMapLayers({
    mapRef: pipMapRef,
    role: 'pip',
    enabled: isPiPVisible,
    styleVersion: pipStyleVersion,
    selectedEvent,
    circuitData,
    trackLineWidth,
    showWindArrows,
    stations,
    tiles,
    currentIndex,
    isCircleOverlayVisible,
    showRadarBbox,
    showRadiusCircles,
    radiusCircleCount,
    radiusCircleDistance,
    extendedTiles,
    radarOpacity,
    fps,
    tilesSource,
    mapTileProvider,
    numericBlobUrl,
    tioTimestamps,
    tioCurrentIndex,
    tioTileField,
    tioActiveLayerRef: pipTioActiveLayerRef,
  });

  return {
    pipMapContainerRef,
    pipMapRef,
    pipStyleVersion,
  };
}
